<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
        <div class="mountains"></div>
      </div>

      <!-- Mobile Sidebar Backdrop -->
      <div 
        v-if="mobileSidebarOpen" 
        class="mobile-sidebar-backdrop"
        @click="toggleMobileSidebar"
      ></div>

      <!-- Sidebar -->
      <div class="glass-card sidebar" :class="{ 'open': mobileSidebarOpen }">
        <div class="back-button-container">
          <ion-button fill="clear" class="back-button" @click="goBackToDashboard">
            <ion-icon :icon="chevronBack"></ion-icon>
            Back to Dashboard
          </ion-button>
          <ion-button fill="clear" class="back-button back-to-calendar-button" @click="closeMobileSidebar">
            <ion-icon :icon="chevronBack"></ion-icon>
            Back to Calendar
          </ion-button>
        </div>
        
        <!-- Spacer for better visual separation -->
        <div class="sidebar-spacer"></div>
        
        <div class="cash-flow-summary">
          <h3 class="section-title">Cash Flow Summary</h3>
          <div class="cash-flow-item">
            <div class="flow-label">Cash Flow IN</div>
            <div class="flow-amount positive" style="color: #22c55e !important;">${{ calendarDaysCreditTotal.toFixed(2) }}</div>
          </div>
          <div class="cash-flow-item">
            <div class="flow-label">Cash Flow OUT</div>
            <div class="flow-amount negative" style="color: #ef4444 !important;">${{ calendarDaysDebitTotal.toFixed(2) }}</div>
          </div>
          <div class="cash-flow-item total">
            <div class="flow-label">Net Flow</div>
            <div class="flow-amount" :class="netFlow >= 0 ? 'positive' : 'negative'" :style="netFlow >= 0 ? 'color: #22c55e !important;' : 'color: #ef4444 !important;'">
              ${{ netFlow.toFixed(2) }}
            </div>
          </div>
        </div>

        <!-- Active Scenario Section -->
        <div class="scenarios-section">
          <h3 class="section-title">Active Scenario</h3>
          
          <!-- Simplified header -->
          <div class="scenarios-header">
            <ion-icon :icon="layersOutline" class="scenarios-icon"></ion-icon>
            <span class="scenarios-subtitle">Select a scenario to display on the calendar</span>
          </div>
          
          <!-- Simplified dropdown container -->
          <div class="scenario-dropdown-container">
            <div 
              class="scenario-dropdown-trigger"
              @click="toggleScenarioDropdown"
            >
              <div class="scenario-trigger-content">
                <span class="trigger-label">Current Scenario:</span>
                <span class="active-scenario-name">{{ selectedScenario?.name || 'None' }}</span>
              </div>
              <ion-icon 
                :icon="chevronDown" 
                class="dropdown-icon"
                :class="{ 'rotated': showScenarioDropdown }"
              ></ion-icon>
            </div>
            
            <!-- Dropdown Menu -->
            <div 
              v-if="showScenarioDropdown"
              class="scenario-dropdown-menu"
              @click.stop
            >
              <div 
                v-for="scenario in allScenarios" 
                :key="scenario.id"
                class="scenario-dropdown-item"
                :class="{ 'active': selectedScenario?.id === scenario.id }"
                @click="handleScenarioSelection(scenario)"
              >
                <ion-label class="scenario-label">{{ scenario.name }}</ion-label>
                <div class="scenario-actions">
                  <ion-icon 
                    v-if="selectedScenario?.id === scenario.id" 
                    :icon="checkmarkCircle" 
                    class="active-scenario-icon"
                  ></ion-icon>
                  <div 
                    v-if="scenario.name.toLowerCase() !== 'default'"
                    @click.stop="openDeleteConfirmationModal(scenario)"
                    class="delete-scenario-btn"
                  >
                    <ion-icon :icon="trashOutline"></ion-icon>
                  </div>
                </div>
              </div>
              
              <!-- Create New Scenario Button -->
              <div class="create-scenario-dropdown-item" @click="handleCreateScenario">
                <ion-icon :icon="addOutline" class="add-icon"></ion-icon>
                <ion-label class="scenario-label">Create New Scenario</ion-label>
              </div>
            </div>
          </div>
        </div>

        <div class="quick-actions">
          <h3 class="section-title">Quick Add</h3>
          <ion-button expand="block" class="login-button" @click="goToAddTransaction('add')">
            Add Transaction
          </ion-button>
        </div>
      </div>

      <div class="calendar-layout">
        <!-- Mobile Menu Toggle Button -->
        <ion-header class="ion-no-border">
          <ion-button 
            class="mobile-menu-toggle"
            fill="clear" 
            @click="toggleMobileSidebar"
          >
            <ion-icon :icon="menuOutline" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-header>
        
        <!-- Desktop Calendar Layout -->
        <div class="desktop-calendar">
          <div class="glass-card calendar-container">
            <div class="calendar-header">
              <ion-button fill="clear" class="nav-button" @click="handlePreviousMonth">
                <ion-icon :icon="chevronBack"></ion-icon>
              </ion-button>
              <h2 class="month-title">{{ currentMonthYear }}</h2>
              <ion-button fill="clear" class="nav-button" @click="handleNextMonth">
                <ion-icon :icon="chevronForward"></ion-icon>
              </ion-button>
            </div>

            <div class="calendar-grid" :class="{ 'transitioning': isTransitioning }">
              <div v-for="day in desktopDaysOfWeek" :key="day" class="calendar-day-header">
                {{ day }}
              </div>
              <transition-group name="calendar-day" tag="div" class="calendar-days-container">
                <div 
                  v-for="date in calendarDays" 
                  :key="`${date.date.getTime()}-${date.currentMonth ? 'current' : 'other'}`"
                  class="calendar-day"
                  :class="{ 
                    'current-month': date.currentMonth, 
                    'has-events': date.hasEvents,
                    'today': date.isToday,
                    'transitioning': isTransitioning
                  }"
                >
                  {{ date.day }}
                  <div v-if="date.events" class="event-details">
                    <div 
                      v-for="(event, index) in date.events" 
                      :key="index" 
                      class="event-item"
                      :class="event.type === 'CREDIT' ? 'positive' : 'negative'"
                      @click="goToAddTransaction(event)"
                    >
                      <div class="event-name">{{ event.name }}</div>
                      <div class="event-amount">${{ getEventDisplayAmount(event) }}</div>
                    </div>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>
        </div>

        <!-- Google Calendar Style Mobile Layout -->
        <div class="google-calendar-mobile">
          <!-- Top Bar -->
          <div class="calendar-top-bar">
            <div class="top-bar-left">
              <span class="current-time">{{ currentTime }}</span>
            </div>
            <div class="top-bar-center">
              <ion-button fill="clear" class="year-nav-button" @click="handlePreviousYear">
                <ion-icon :icon="chevronBack"></ion-icon>
              </ion-button>
              <span class="current-year">{{ currentYear }}</span>
              <ion-button fill="clear" class="year-nav-button" @click="handleNextYear">
                <ion-icon :icon="chevronForward"></ion-icon>
              </ion-button>
            </div>
          </div>

          <!-- Month Name -->
          <div class="month-name-header">
            <h1 class="month-title-large">{{ currentMonthName }}</h1>
          </div>

          <!-- Calendar -->
          <div class="glass-card calendar-container">
            <!-- Weekday Headers -->
            <div class="calendar-weekday-headers">
              <div v-for="day in daysOfWeek" :key="day" class="weekday-header">
                {{ day }}
              </div>
            </div>

            <!-- Calendar Grid -->
            <div class="calendar-grid" :class="{ 'transitioning': isTransitioning }">
              <transition-group name="calendar-day" tag="div" class="calendar-days-container">
                <div 
                  v-for="date in calendarDays" 
                  :key="`${date.date.getTime()}-${date.currentMonth ? 'current' : 'other'}`"
                  class="calendar-day"
                  :class="{ 
                    'current-month': date.currentMonth, 
                    'has-events': date.hasEvents,
                    'today': date.isToday,
                    'transitioning': isTransitioning
                  }"
                >
                  <span class="day-number">{{ date.day }}</span>
                  <div v-if="date.events && date.events.length > 0" class="event-tags">
                    <div 
                      v-for="(event, index) in getVisibleEvents(date)" 
                      :key="index" 
                      class="event-tag"
                      :class="event.type === 'CREDIT' ? 'event-tag-positive' : 'event-tag-negative'"
                      @click.stop="goToAddTransaction(event)"
                      :title="event.name"
                    >
                      {{ truncateEventName(event.name) }}
                    </div>
                    <div 
                      v-if="date.events.length > 3 && !isDateExpanded(date)" 
                      class="event-tag event-tag-more"
                      @click.stop="toggleDateExpansion(date)"
                    >
                      +{{ date.events.length - 3 }}
                    </div>
                    <div 
                      v-if="date.events.length > 3 && isDateExpanded(date)" 
                      class="event-tag event-tag-collapse"
                      @click.stop="toggleDateExpansion(date)"
                    >
                      Show less
                    </div>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>

          <!-- Bottom Bar -->
          <div class="calendar-bottom-bar">
            <ion-button class="today-button" @click="goToToday">
              Today
            </ion-button>
            <div class="bottom-nav-left">
              <ion-button fill="clear" class="nav-button-small" @click="handlePreviousMonth">
                <ion-icon :icon="chevronBack"></ion-icon>
              </ion-button>
              <span class="next-month-preview">{{ nextMonthPreview }}</span>
              <ion-button fill="clear" class="nav-button-small" @click="handleNextMonth">
                <ion-icon :icon="chevronForward"></ion-icon>
              </ion-button>
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonModal, 
         IonButtons, IonBackButton, IonIcon, IonPopover, IonList, IonItem, IonLabel, IonAlert, IonCheckbox, IonRadio, IonItemDivider } from '@ionic/vue';
import { chevronBack, chevronForward, addOutline, layersOutline, trashOutline, checkmarkCircle, chevronDown, menuOutline, searchOutline, listOutline, gridOutline } from 'ionicons/icons';
import { useCalendarStore } from '@/store/calendar';
import { useScenariosStore } from '@/store/scenarios';
import { useEventsStore } from '@/store/events';
import { mapState, mapActions } from 'pinia';
import axios from 'axios';

export default {
  name: 'CalendarPage',
  components: {
    IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonModal,
    IonButtons, IonBackButton, IonIcon, IonPopover, IonList, IonItem, IonLabel,
    IonAlert, IonCheckbox, IonRadio, IonItemDivider
  },
  setup() {
    const store = useCalendarStore();
    const scenariosStore = useScenariosStore();
    const eventsStore = useEventsStore();
    return { store, scenariosStore, eventsStore };
  },
  data() {
    return {
      daysOfWeek: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      chevronBack,
      chevronForward,
      addOutline,
      layersOutline,
      trashOutline,
      checkmarkCircle,
      chevronDown,
      menuOutline,
      searchOutline,
      listOutline,
      gridOutline,
      localCurrentDate: new Date(),
      isTransitioning: false,
      showScenarioDropdown: false,
      isInitializing: false,
      mobileSidebarOpen: false,
      currentTime: '',
      isListView: false,
      timeInterval: null,
      expandedDates: new Set()
    }
  },
  computed: {
    ...mapState(useCalendarStore, [
      'netFlow',
      'currentMonthYear',
      'profile',
      'currentDate',
      'calendarDays',
      'calendarDaysDebitTotal',
      'calendarDaysCreditTotal'
    ]),
    currentYear() {
      return new Date(this.currentDate).getFullYear();
    },
    currentMonthName() {
      const date = new Date(this.currentDate);
      return date.toLocaleString('default', { month: 'long' });
    },
    nextMonthPreview() {
      const date = new Date(this.currentDate);
      date.setMonth(date.getMonth() + 1);
      return date.toLocaleString('default', { month: 'short' });
    },
    desktopDaysOfWeek() {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },
    ...mapState(useScenariosStore, [
      'allScenarios',
      'selectedScenario',
      'defaultScenario',
      'isDefaultScenario',
      'customScenarios',
      'hasScenarios',
      'isLoading',
      'hasError'
    ]),
    ...mapState(useEventsStore, [
      'allEvents',
      'monthlyEvents',
      'filteredEvents',
      'activeEvents',
      'cashFlowInTotal',
      'cashFlowOutTotal'
    ]),

  },
  methods: {
    ...mapActions(useCalendarStore, [
      'fetchProfileInfo',
      'previousMonth',
      'nextMonth'
    ]),
    getEventDisplayAmount(event) {
      // If the amount is already calculated (from calendar store), use it directly
      if (event.amount !== undefined && event.amount !== null) {
        return event.amount;
      }
      
      // Fallback to calculating from monthly_payment and escrow
      const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
      if (loanCategories.includes(event.category) && event.monthly_payment && event.monthly_payment > 0) {
        // For mortgages, add escrow to the monthly payment
        if (event.category === 'MORTGAGE' && event.escrow && event.escrow > 0) {
          return parseFloat(event.monthly_payment) + parseFloat(event.escrow);
        }
        return event.monthly_payment;
      }
      return event.amount;
    },
    ...mapActions(useScenariosStore, [
      'fetchScenarios',
      'selectScenario',
      'selectDefaultScenario',
      'deleteScenario',
      'createScenario'
    ]),
    ...mapActions(useEventsStore, [
      'fetchEvents',
      'fetchEventsForMonthByScenario',
      'createEvent',
      'updateEvent',
      'deleteEvent'
    ]),
    goToAddTransaction(event) {
      // Close mobile sidebar if open
      this.closeMobileSidebar();
      
      // console.log('goToAddTransaction called with event:', event);
      // console.log('selectedScenario:', this.selectedScenario);
      // console.log('selectedScenario.id:', this.selectedScenario?.id);
      // console.log('profile.id:', this.profile?.id);
      
      const query = { 
        eventID: event && event.id ? event.id : undefined,
        profileID: this.profile.id,
        scenarioID: this.selectedScenario.id
      };
      
      // console.log('Navigating to transaction page with query:', query);
      
      this.$router.push({
        path: '/transaction',
        query: query,
      });
    },
    async handlePreviousMonth() {
      this.isTransitioning = true;
      this.expandedDates.clear(); // Clear expanded dates when changing months
      await this.previousMonth();
      this.localCurrentDate = new Date(this.currentDate);
      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    },
    async handleNextMonth() {
      this.isTransitioning = true;
      this.expandedDates.clear(); // Clear expanded dates when changing months
      await this.nextMonth();
      this.localCurrentDate = new Date(this.currentDate);
      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    },
    goToCreateScenarioPage() {
      this.$router.push({
        path: '/create-scenario',
        query: { profileID: this.profile?.id }
      });
    },
    openDeleteConfirmationModal(scenario) {
      this.closeScenarioDropdown();
      this.$router.push({
        path: '/delete-scenario',
        query: {
          scenarioId: scenario.id,
          scenarioName: scenario.name,
          scenarioDescription: scenario.description,
          profileID: this.profile?.id
        }
      });
    },

    toggleScenarioDropdown() {
      this.showScenarioDropdown = !this.showScenarioDropdown;
    },
    
    closeScenarioDropdown() {
      this.showScenarioDropdown = false;
    },
    
    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
      const sidebar = document.querySelector('.glass-card.sidebar');
      if (sidebar) {
        if (this.mobileSidebarOpen) {
          sidebar.classList.add('open');
          document.body.classList.add('sidebar-open');
        } else {
          sidebar.classList.remove('open');
          document.body.classList.remove('sidebar-open');
        }
      }
    },
    
    closeMobileSidebar() {
      this.mobileSidebarOpen = false;
      const sidebar = document.querySelector('.glass-card.sidebar');
      if (sidebar) {
        sidebar.classList.remove('open');
      }
      document.body.classList.remove('sidebar-open');
    },
    
    handleScenarioSelection(scenario) {
      this.selectScenario(scenario);
      this.closeScenarioDropdown();
    },
    
    handleClickOutside(event) {
      // Close dropdown if clicking outside
      const dropdownContainer = event.target.closest('.scenario-dropdown-container');
      if (!dropdownContainer && this.showScenarioDropdown) {
        this.closeScenarioDropdown();
      }
    },
    
    handleCreateScenario() {
      this.closeScenarioDropdown();
      this.goToCreateScenarioPage();
    },
    
    async goBackToDashboard() {
      // Close mobile sidebar if open
      this.closeMobileSidebar();
      
      // Reset selected scenario back to Default before going back to dashboard
      try {
        await this.selectDefaultScenario();
      } catch (error) {
        console.error('Error resetting to default scenario:', error);
      }
      
      // Navigate back to dashboard
      this.$router.push('/dashboard');
    },
    handlePreviousYear() {
      const date = new Date(this.currentDate);
      date.setFullYear(date.getFullYear() - 1);
      this.store.setCalendarToDate(date.getFullYear(), date.getMonth(), date.getDate());
      this.localCurrentDate = new Date(date);
    },
    handleNextYear() {
      const date = new Date(this.currentDate);
      date.setFullYear(date.getFullYear() + 1);
      this.store.setCalendarToDate(date.getFullYear(), date.getMonth(), date.getDate());
      this.localCurrentDate = new Date(date);
    },
    goToToday() {
      const today = new Date();
      this.store.setCalendarToDate(today.getFullYear(), today.getMonth(), today.getDate());
      this.localCurrentDate = new Date(today);
    },
    toggleListView() {
      this.isListView = !this.isListView;
    },
    updateCurrentTime() {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      this.currentTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
    },
    truncateEventName(name) {
      if (!name) return '';
      // Truncate to fit in the tag, similar to Google Calendar
      if (name.length > 10) {
        return name.substring(0, 10) + '...';
      }
      return name;
    },
    getDateKey(date) {
      // Create a unique key for the date
      return `${date.date.getFullYear()}-${date.date.getMonth()}-${date.date.getDate()}`;
    },
    isDateExpanded(date) {
      return this.expandedDates.has(this.getDateKey(date));
    },
    toggleDateExpansion(date) {
      const key = this.getDateKey(date);
      if (this.expandedDates.has(key)) {
        this.expandedDates.delete(key);
      } else {
        this.expandedDates.add(key);
      }
      // Force reactivity
      this.$forceUpdate();
    },
    getVisibleEvents(date) {
      if (this.isDateExpanded(date)) {
        return date.events;
      }
      return date.events.slice(0, 3);
    }


  },
  watch: {
    currentDate: {
      handler(newVal) {
        this.localCurrentDate = new Date(newVal);
      },
      immediate: true
    },
    events: {
      handler(newVal) {
        // Events changed - no action needed
      },
      immediate: true
    },
    selectedScenario: {
      handler(newVal, oldVal) {
        // Only act if we have actual data and it's a real change (not initial load)
        if (newVal && newVal.id && oldVal && newVal.id !== oldVal.id) {
          // Selected scenario actually changed - ensure calendar is updated
          this.fetchEventsForMonthByScenario().then(() => {
            // Log scenario change with events AFTER they've loaded
            console.log('=== SCENARIO CHANGED ===');
            console.log('New Active Scenario:', newVal.name || 'None');
            console.log('Events loaded for scenario:', this.monthlyEvents || []);
            console.log('=== END SCENARIO CHANGED ===');
          });
        }
      },
      immediate: false
    },
    monthlyEvents: {
      handler(newVal, oldVal) {
        // Only act if we have actual data and it's a real change
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          // Check if this is a real change, not just the same data being set again
          if (!oldVal || JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
            // Events actually changed - update calendar days
            this.store.updateCalendarDays();
          }
        }
      },
      immediate: false
    },
    // Watch for route query changes to detect when returning from transaction page
    '$route.query': {
      handler(newQuery, oldQuery) {
        // Skip if we're still initializing
        if (this.isInitializing) return;
        
        // If refresh parameter is present, refresh the events
        if (newQuery.refresh && newQuery.refresh !== oldQuery?.refresh) {
          if (this.selectedScenario) {
            this.fetchEventsForMonthByScenario();
            this.store.updateCalendarDays();
          }
        }
        
        // If scenarioID changed, switch to the new scenario
        if (newQuery.scenarioID && newQuery.scenarioID !== oldQuery?.scenarioID) {
          const targetScenario = this.allScenarios.find(s => s.id === newQuery.scenarioID);
          if (targetScenario && targetScenario.id !== this.selectedScenario?.id) {
            this.selectScenario(targetScenario).then(() => {
              this.fetchEventsForMonthByScenario();
              this.store.updateCalendarDays();
            });
          }
        }
      },
      immediate: true
    }
  },
  activated() {
    // Prevent redundant calls if we're already initializing
    if (this.isInitializing) return;
    
    // Only refresh if we don't have scenarios loaded or if we're returning from a transaction
    // This prevents redundant calls when the page is already properly loaded
    if (!this.hasScenarios) {
      this.fetchScenarios();
    }
    
    // Only fetch events if we don't have them or if scenario changed
    if (this.selectedScenario && (!this.monthlyEvents || this.monthlyEvents.length === 0)) {
      this.fetchEventsForMonthByScenario();
      this.store.updateCalendarDays();
    }
  },
  async mounted() {
    // Update current time immediately and set up interval
    this.updateCurrentTime();
    this.timeInterval = setInterval(() => {
      this.updateCurrentTime();
    }, 60000); // Update every minute
    
    // Prevent redundant initialization
    if (this.isInitializing) return;
    this.isInitializing = true;
    
    // Ensure calendar data is loaded when the page is mounted
    try {
      // Check if we have the necessary data loaded
      if (!this.profile || !this.selectedScenario) {
        // Try to get profile from route query or localStorage
        const profileID = this.$route.query.profileID || localStorage.getItem('userID');
        if (profileID) {
          await this.fetchProfileInfo(profileID);
          
          // Ensure scenarios are loaded and a scenario is selected
          if (!this.hasScenarios) {
            await this.fetchScenarios();
          }
          
          // Check if we have a specific scenarioID from the route query
          const routeScenarioID = this.$route.query.scenarioID;
          if (routeScenarioID && this.hasScenarios) {
            // Find and select the scenario from the route
            const targetScenario = this.allScenarios.find(s => s.id === routeScenarioID);
            if (targetScenario) {
              await this.selectScenario(targetScenario);
            } else {
              await this.selectDefaultScenario();
            }
          } else if (!this.selectedScenario && this.hasScenarios) {
            // If no scenario selected and no route scenarioID, try to select default
            await this.selectDefaultScenario();
          }
          
          // Only fetch events if we have a selected scenario
          if (this.selectedScenario) {
            await this.fetchEventsForMonthByScenario();
            // Update calendar days after fetching events
            this.store.updateCalendarDays();
          } else {
            console.error('No scenario selected, cannot fetch events');
          }
        }
      } else {
        // Check if we need to switch to a different scenario from route
        const routeScenarioID = this.$route.query.scenarioID;
        if (routeScenarioID && routeScenarioID !== this.selectedScenario.id) {
          const targetScenario = this.allScenarios.find(s => s.id === routeScenarioID);
          if (targetScenario) {
            await this.selectScenario(targetScenario);
            await this.fetchEventsForMonthByScenario();
            this.store.updateCalendarDays();
          }
        } else {
          await this.fetchEventsForMonthByScenario();
          // Update calendar days after fetching events
          this.store.updateCalendarDays();
        }
      }
      
      // Log only essential information
      console.log('=== CALENDAR PAGE LOADED ===');
      console.log('Active Scenario:', this.selectedScenario?.name || 'None');
      console.log('Events loaded for scenario:', this.monthlyEvents || []);
      console.log('=== END CALENDAR PAGE LOADED ===');
    } finally {
      this.isInitializing = false;
    }
    
    // Add click outside handler
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeUnmount() {
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);
    // Clear time interval
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }
}
</script>

<style scoped>
/* Google Calendar Mobile Styles */
.google-calendar-mobile {
  width: 100%;
  min-height: 100vh;
  background: #202124;
  color: #ffffff;
  overflow-x: hidden;
  box-sizing: border-box;
  max-width: 100vw;
  padding-top: 3rem;
}

/* Ensure calendar layout doesn't overflow */
.calendar-layout {
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* Top Bar */
.calendar-top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #202124;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.top-bar-left {
  flex: 1;
}

.current-time {
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
}

.top-bar-center {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
}

.year-nav-button {
  --color: #ffffff;
  --padding-start: 0.25rem;
  --padding-end: 0.25rem;
  margin: 0;
  height: auto;
}

.current-year {
  font-size: 0.9rem;
  color: #ffffff;
  font-weight: 500;
}

.top-bar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(60, 60, 60, 0.6);
  padding: 0.35rem 0.75rem;
  border-radius: 24px;
  flex: 1;
  justify-content: flex-end;
}

.action-button {
  --color: #ffffff;
  --padding-start: 0.5rem;
  --padding-end: 0.5rem;
  margin: 0;
  height: auto;
}

/* Month Name Header */
.month-name-header {
  padding: 1rem 1rem 0.75rem 1rem;
  background: #202124;
}

.month-title-large {
  font-size: 2.25rem;
  font-weight: 400;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

/* Calendar Container */
.calendar-container {
  background: #202124 !important;
  padding: 0.5rem 0.3rem 1rem 0.3rem !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Weekday Headers */
.calendar-weekday-headers {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.07rem;
  margin-bottom: 0.4rem;
  width: 100%;
  box-sizing: border-box;
}

.weekday-header {
  text-align: center;
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  padding: 0.35rem 0;
}

/* Calendar Grid */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.07rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Mobile calendar day styles - only apply on mobile Google Calendar layout */
@media (max-width: 768px) {
  .google-calendar-mobile .calendar-day {
    min-height: 110px;
    padding: 0.45rem 0.1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
}

/* Mobile-only styles - scoped to Google Calendar mobile layout */
@media (max-width: 768px) {
  .google-calendar-mobile .calendar-day.current-month {
    background: rgba(255, 255, 255, 0.05);
  }

  .google-calendar-mobile .calendar-day:not(.current-month) {
    opacity: 0.3;
    background: rgba(255, 255, 255, 0.02);
  }

  .google-calendar-mobile .day-number {
    font-size: 0.8rem;
    font-weight: 400;
    color: #ffffff;
    margin-bottom: 0.15rem;
    align-self: flex-start;
    line-height: 1;
  }

  .google-calendar-mobile .calendar-day.today {
    border: 2px solid rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.08);
  }

  .google-calendar-mobile .calendar-day.today .day-number {
    background: #ea4335;
    color: #ffffff;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    margin-bottom: 0.15rem;
    font-size: 0.75rem;
  }
}

/* Event Tags */
.event-tags {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  width: 100%;
  margin-top: 0.1rem;
  box-sizing: border-box;
}

.event-tag {
  font-size: 0.55rem;
  padding: 0.08rem 0.25rem;
  border-radius: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  font-weight: 400;
  line-height: 1.15;
  max-width: 100%;
  box-sizing: border-box;
}

.event-tag-positive {
  background: #22c55e;
  color: #ffffff;
}

.event-tag-negative {
  background: #ea4335;
  color: #ffffff;
}

.event-tag-more {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
}

.event-tag-collapse {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
}

/* Bottom Bar */
.calendar-bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #202124;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  position: sticky;
  bottom: 0;
}

.today-button {
  --background: rgba(255, 255, 255, 0.1);
  --color: #ffffff;
  --border-radius: 20px;
  --padding-start: 1.5rem;
  --padding-end: 1.5rem;
  --padding-top: 0.75rem;
  --padding-bottom: 0.75rem;
  font-weight: 500;
  text-transform: none;
}

.bottom-nav-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
}

.nav-button-small {
  --color: #ffffff;
  --padding-start: 0.25rem;
  --padding-end: 0.25rem;
  margin: 0;
  height: auto;
}

.next-month-preview {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Desktop - Hide Google Calendar Mobile Styles */
@media (min-width: 769px) {
  .google-calendar-mobile {
    display: none;
  }
  
  .desktop-calendar {
    display: block;
  }
  
  /* Hide mobile menu toggle on desktop */
  .mobile-menu-toggle {
    display: none !important;
  }
  
  /* Hide mobile header on desktop and ensure it doesn't overlap */
  .calendar-layout ion-header {
    display: none !important;
    position: absolute !important;
    visibility: hidden !important;
    pointer-events: none !important;
    z-index: -1 !important;
  }
  
  /* Ensure calendar-layout doesn't overlap sidebar */
  .calendar-layout {
    position: relative !important;
  }
  
  /* Ensure sidebar is on top and not blocked */
  .glass-card.sidebar {
    position: fixed !important;
    z-index: 1001 !important;
    top: 0 !important;
    left: 0 !important;
  }
  
  /* Hide "Back to Calendar" button on desktop */
  .glass-card.sidebar .back-to-calendar-button {
    display: none !important;
  }
  
  /* Ensure back button container is visible and accessible */
  .glass-card.sidebar .back-button-container {
    position: relative !important;
    z-index: 1002 !important;
    pointer-events: auto !important;
  }
  
  .glass-card.sidebar .back-button {
    position: relative !important;
    z-index: 1003 !important;
    pointer-events: auto !important;
  }
  
  /* Ensure nothing overlaps the sidebar area */
  .welcome-content,
  ion-content {
    overflow: visible !important;
  }
  
  .calendar-layout {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
  
  /* Desktop calendar - ensure mobile styles don't apply */
  .desktop-calendar .calendar-container {
    background: transparent !important;
    padding: 1rem !important;
  }
  
  /* Reset any mobile calendar day styles for desktop */
  .desktop-calendar .calendar-day {
    min-height: auto !important;
    max-height: none !important;
    padding: 0.2rem !important;
    overflow: visible !important;
    /* Let global.css handle the rest */
  }
  
  /* Ensure desktop uses original event display */
  .desktop-calendar .event-details {
    display: block !important;
  }
  
  .desktop-calendar .event-tags {
    display: none !important;
  }
  
  .desktop-calendar .day-number {
    display: none !important;
  }
  
  /* Ensure sidebar is accessible */
  .glass-card.sidebar {
    z-index: 1000 !important;
  }
  
  /* Fix back button text rendering on desktop */
  .glass-card.sidebar .back-button {
    text-rendering: optimizeLegibility !important;
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale !important;
    text-shadow: none !important;
    transform: none !important;
    filter: none !important;
    backface-visibility: hidden !important;
    -webkit-backface-visibility: hidden !important;
  }
  
  .glass-card.sidebar .back-button * {
    text-shadow: none !important;
    transform: none !important;
    filter: none !important;
  }
  
  .glass-card.sidebar .back-button .button-native {
    text-shadow: none !important;
    transform: none !important;
    filter: none !important;
    backface-visibility: hidden !important;
  }
  
  .glass-card.sidebar .back-button ion-label {
    text-shadow: none !important;
    transform: none !important;
    filter: none !important;
  }
}

/* Mobile - Hide Desktop Calendar */
@media (max-width: 768px) {
  .desktop-calendar {
    display: none;
  }
}

/* Mobile - Show Google Calendar Mobile Styles */
@media (max-width: 768px) {
  .google-calendar-mobile {
    display: block;
  }
  
  /* Hide original calendar header */
  .calendar-header {
    display: none !important;
  }
  
  /* Adjust calendar container */
  .glass-card.calendar-container {
    background: #202124 !important;
    padding: 0.5rem 0.3rem 1rem 0.3rem !important;
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }
  
  /* Override existing calendar day styles for mobile */
  .calendar-day {
    min-height: 105px !important;
    padding: 0.45rem 0.1rem !important;
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 4px !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }
  
  .calendar-grid {
    gap: 0.06rem !important;
    max-width: 100% !important;
  }
  
  .calendar-weekday-headers {
    gap: 0.06rem !important;
    max-width: 100% !important;
  }
  
  .google-calendar-mobile {
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }
  
  .calendar-layout {
    max-width: 100vw !important;
    overflow-x: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  
  ion-content.welcome-content {
    --padding-start: 0 !important;
    --padding-end: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  
  .calendar-day.current-month {
    background: rgba(255, 255, 255, 0.05) !important;
  }
  
  .calendar-day:not(.current-month) {
    opacity: 0.3 !important;
    background: rgba(255, 255, 255, 0.02) !important;
  }
  
  .calendar-day.today {
    background: rgba(255, 255, 255, 0.08) !important;
    border: 2px solid rgba(255, 255, 255, 0.9) !important;
  }
  
  .calendar-day-header {
    display: none !important;
  }
  
  /* Hide event details in favor of event tags */
  .event-details {
    display: none !important;
  }
  
  /* Reduce spacing between back buttons */
  .glass-card.sidebar .back-button-container {
    gap: 0.1rem !important;
  }
  
  .glass-card.sidebar .back-button {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
  }
  
  /* Show "Back to Calendar" button on mobile */
  .glass-card.sidebar .back-to-calendar-button {
    display: flex !important;
  }
}
</style>